import * as Sentry from '@sentry/browser'
import moment from 'moment-timezone'

import { onLogout } from '@/lib/apollo'

import profile from '@/graphql/queries/users/profile.gql'
import featureFlags from '@/graphql/queries/accounts/featureFlags.gql'
import savedCommentReplies from '@/graphql/queries/posts/savedCommentReplies.gql'
import setPreference from '@/graphql/mutations/users/setPreference.gql'
import setPreferences from '@/graphql/mutations/users/setPreferences.gql'
import logout from '@/graphql/mutations/auth/logout.gql'
import setTimezone from '@/graphql/mutations/users/setTimezone.gql'

import { BILLING_TERM_YEAR } from '@/lib/constants'
import {
  PLATFORM_CODE_REDDIT,
  PLATFORM_CODE_TWITTER,
  PLATFORM_CODE_THREADS,
  PLATFORM_CODE_INSTAGRAM,
  PLATFORM_CODE_FACEBOOK,
  PLATFORM_CODE_TIKTOK,
  PLATFORM_CODE_LINKEDIN,
  PLATFORM_CODE_YOUTUBE,
  PLATFORM_CODE_PINTEREST,
  PLATFORM_CODE_BLUESKY,
  PLATFORM_CODE_TUMBLR,
  PLATFORM_CODE_MASTODON,
  platforms
} from '@/lib/platforms'
import { getLocalPreferences } from '@/lib/prefs'
import { isUnlimited } from '@/lib/plans'
import { getBrowserTimezone, pluralize } from '@/lib/utils'

export const state = () => ({
  hasLoadedProfile: false,
  finishedInitialLoad: false,
  postponeHasUpdate: false,
  startedTour: false,
  finishedTour: false,
  profile: null,
  featureFlags: null,
  savedCommentReplies: [],
  totalUnreadCount: 0
})

export const mutations = {
  finishedInitialLoad(state) {
    state.finishedInitialLoad = true
  },
  setPostponeUpdated(state) {
    state.postponeHasUpdate = true
  },
  startTour(state) {
    state.startedTour = true
  },
  finishTour(state) {
    state.finishedTour = true
  },
  profile(state, profile) {
    state.profile = profile
    state.hasLoadedProfile = true
  },
  featureFlags(state, featureFlags) {
    state.featureFlags = featureFlags
  },
  preferences(state, preferences) {
    state.profile.preferences = preferences
  },
  preference(state, { key, value }) {
    state.profile.preferences[key] = value
  },
  checkOnboardingItem(state, onboardingItemName) {
    state.profile[onboardingItemName] = true
  },
  // Saved comment replies
  setSavedCommentReplies(state, savedCommentReplies) {
    state.savedCommentReplies = savedCommentReplies
  },
  addSavedCommentReply(state, savedCommentReply) {
    state.savedCommentReplies.push(savedCommentReply)
  },
  removeSavedCommentReply(state, savedCommentReplyId) {
    state.savedCommentReplies = state.savedCommentReplies.filter(
      (reply) => reply.id !== savedCommentReplyId
    )
  },
  totalUnreadCount(state, totalUnreadCount) {
    state.totalUnreadCount = Math.max(totalUnreadCount, 0)
  }
}

export const actions = {
  async logout(context) {
    const apolloClient = this.app.apolloProvider.defaultClient
    const noAuthApolloClient = this.app.apolloProvider.clients.noauth
    await noAuthApolloClient.mutate({ mutation: logout })
    await onLogout(noAuthApolloClient)
    await onLogout(apolloClient)

    context.commit('profile', null)

    Sentry.configureScope(function (scope) {
      scope.setUser(null)
    })
  },
  async loadFeatureFlags(context) {
    const noAuthApolloClient = this.app.apolloProvider.clients.noauth
    const response = await noAuthApolloClient.query({
      query: featureFlags,
      fetchPolicy: 'network-only'
    })

    const flags = response.data.featureFlags
    context.commit(
      'featureFlags',
      flags.reduce((acc, flag) => {
        acc[flag.name] = flag.enabled
        return acc
      }, {})
    )
  },
  async getProfile(context) {
    const apolloClient = this.app.apolloProvider.defaultClient
    const noAuthApolloClient = this.app.apolloProvider.clients.noauth
    const response = await noAuthApolloClient.query({
      query: profile,
      fetchPolicy: 'network-only'
    })

    const userProfile = response.data.profile
    context.commit('profile', userProfile)

    let timezone = getBrowserTimezone()
    if (userProfile && userProfile.timezone) {
      timezone = userProfile.timezone
    }
    moment.tz.setDefault(timezone)

    if (userProfile) {
      if (Object.keys(userProfile.preferences).length === 0) {
        // Upload local preferences to backend if the user has no preferences
        const localPreferences = getLocalPreferences()
        if (Object.keys(localPreferences).length > 0) {
          context.dispatch('setPreferences', localPreferences)
        }
      }
    }

    if (userProfile) {
      // User is logged in
      const isImpersonating = userProfile.isImpersonating
      if (HelpCrunch && !this.app.$config.dev && !isImpersonating) {
        HelpCrunch('updateUser', {
          user_id: userProfile.hashedId,
          email: userProfile.email,
          name:
            userProfile.account.name ||
            userProfile.fullName ||
            userProfile.username,
          custom_data: {
            username: userProfile.username,
            planCode: userProfile.account.plan.code,
            planLabel: userProfile.account.plan.label,
            planMonthlyPrice: userProfile.account.plan.monthlyPrice,
            hasPremiumSupport: userProfile.account.hasPremiumSupport,
            registeredAt: userProfile.registeredAt,
            timezone: userProfile.timezone,
            numRedditAccounts: userProfile.redditAccounts.length,
            billingTerm: userProfile.account.billingTerm,
            subscriptionStatus: userProfile.account.subscriptionStatus,
            stripeCustomerId: userProfile.account.stripeCustomerId,
            stripePlanId:
              userProfile.account.plan.stripeMonthlyId ||
              userProfile.account.plan.stripeAnnualId,
            appVersion: this.app.$config.appVersion
          }
        })
      }

      Sentry.configureScope(function (scope) {
        scope.setUser({
          id: userProfile.id,
          email: userProfile.email,
          username: userProfile.username
        })
      })
    }

    // Set the user's initial timezone
    if (userProfile && !userProfile.timezone) {
      apolloClient.mutate({
        mutation: setTimezone,
        variables: {
          timezone: timezone
        }
      })
    }
  },
  async finishedInitialLoad(context) {
    context.commit('finishedInitialLoad')
  },
  async loadSavedCommentReplies(context) {
    let response = await this.app.apolloProvider.defaultClient.query({
      query: savedCommentReplies,
      fetchPolicy: 'network-only',
      variables: {
        platform: 'REDDIT'
      }
    })
    context.commit('setSavedCommentReplies', response.data.savedCommentReplies)
  },
  async setPreferences(context, preferences) {
    const result = await this.app.apolloProvider.defaultClient.mutate({
      mutation: setPreferences,
      variables: {
        preferences: JSON.stringify(preferences)
      }
    })
    if (result.data.setPreferences.success) {
      context.commit('preferences', result.data.setPreferences.preferences)
    }
  },
  async setPreference(context, { key, value }) {
    context.commit('preference', { key, value })
    const result = await this.app.apolloProvider.defaultClient.mutate({
      mutation: setPreference,
      variables: {
        key: key,
        value: value
      }
    })

    if (result.data.setPreference.success) {
      context.commit('preferences', result.data.setPreference.preferences)
    }
  },
  incrementTotalUnreadCount(context, { amount }) {
    context.commit('totalUnreadCount', context.state.totalUnreadCount + amount)
  }
}

const platformToFlag = {
  [PLATFORM_CODE_TWITTER]: 'tootin',
  [PLATFORM_CODE_THREADS]: 'zuckin',
  [PLATFORM_CODE_INSTAGRAM]: 'grammin',
  [PLATFORM_CODE_FACEBOOK]: 'bookface',
  [PLATFORM_CODE_TIKTOK]: 'grammin',
  [PLATFORM_CODE_LINKEDIN]: 'linkin',
  [PLATFORM_CODE_YOUTUBE]: 'tubin',
  [PLATFORM_CODE_PINTEREST]: 'pinnin',
  [PLATFORM_CODE_TUMBLR]: 'tumblin',
  [PLATFORM_CODE_MASTODON]: 'mastin'
}

export const getters = {
  plan(state) {
    if (state.profile) {
      return state.profile.account.plan
    } else {
      return null
    }
  },
  isAccountOwner(state) {
    if (state.profile) {
      return (
        state.profile.id.toString() === state.profile.account.ownerId.toString()
      )
    } else {
      return null
    }
  },
  userTimezone(state) {
    if (state.profile && state.profile.timezone) {
      return state.profile.timezone
    } else {
      return getBrowserTimezone()
    }
  },
  isOnYearlyBillingTerm(state) {
    if (state.profile) {
      return state.profile.account.billingTerm === BILLING_TERM_YEAR
    } else {
      return false
    }
  },
  isLoggedIn(state) {
    return !!state.profile
  },
  // Feature flags
  isPlatformFeatureEnabled: (state) => (platform) => {
    // These platforms never had feature flags
    if (['reddit', 'bluesky'].includes(platform)) {
      return true
    }
    if (!state.featureFlags) {
      return false
    }
    const flag = platformToFlag[platform]
    if (!flag) {
      return false
    }
    return state.featureFlags[flag]
  },
  twitterFeatureEnabled(state, getters) {
    return getters.isPlatformFeatureEnabled(PLATFORM_CODE_TWITTER)
  },
  instagramFeatureEnabled(state, getters) {
    return getters.isPlatformFeatureEnabled(PLATFORM_CODE_INSTAGRAM)
  },
  facebookFeatureEnabled(state, getters) {
    return getters.isPlatformFeatureEnabled(PLATFORM_CODE_FACEBOOK)
  },
  tiktokFeatureEnabled(state, getters) {
    return getters.isPlatformFeatureEnabled(PLATFORM_CODE_TIKTOK)
  },
  pinterestFeatureEnabled(state, getters) {
    return getters.isPlatformFeatureEnabled(PLATFORM_CODE_PINTEREST)
  },
  linkedinFeatureEnabled(state, getters) {
    return getters.isPlatformFeatureEnabled(PLATFORM_CODE_LINKEDIN)
  },
  groupedViewFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.grpdvw
  },
  aiAssistantFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.aiasst
  },
  clientManagerFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.cms
  },
  unifiedPostSchedulerFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.ups
  },
  postTimeRecommendationsFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.ptr
  },
  instagramFirstCommentFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.igfc
  },
  instagramLoginFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.iglogin
  },
  instagramLoginWithFacebookFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.igfblogin
  },
  conversationsFeatureEnabled(state) {
    return state.featureFlags && state.featureFlags.convo
  },
  enabledPlatforms(state, getters) {
    return platforms.filter((platform) =>
      getters.isPlatformFeatureEnabled(platform.code)
    )
  },
  connectedPlatforms(state, getters) {
    if (!state.profile) {
      return []
    }
    return platforms
      .filter((platform) => getters.isConnectedToPlatform(platform.code))
      .map((platform) => platform.code)
  },
  isConnectedToPlatform: (state, getters) => (platform) => {
    if (!state.profile) {
      return false
    }
    return state.profile.socialAccounts.some(
      (a) => a.platform.toLowerCase() === platform.toLowerCase()
    )
  },
  isConnectedToNonRedditPlatform(state, getters) {
    return (
      getters.connectedPlatforms.length > 0 &&
      getters.connectedPlatforms.filter((platform) => platform !== 'reddit')
        .length > 0
    )
  },
  // Reddit
  linkedRedditAccounts(state) {
    if (!state.profile) {
      return []
    }
    return state.profile.redditAccounts
  },
  isConnectedToReddit(state, getters) {
    return getters.linkedRedditAccounts.length > 0
  },
  redditSocialAccounts(state) {
    return state.profile.socialAccounts.filter(
      (a) => a.platform.toLowerCase() === PLATFORM_CODE_REDDIT
    )
  },
  // Twitter
  hasFullTwitterAccess(state) {
    if (!state.profile) {
      return false
    }
    return state.profile.account.allowedMonthlyTweets > 5
  },
  tweetsRemainingThisPeriod(state) {
    if (!state.profile) {
      return 0
    }
    return (
      state.profile.account.allowedMonthlyTweets -
      state.profile.account.tweetsSubmittedThisMonth
    )
  },
  twitterAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_TWITTER)
  },
  isConnectedToTwitter(state, getters) {
    return getters.twitterAccounts.length > 0
  },
  monthlyTweetUsagePercentage(state) {
    if (!state.profile) {
      return 0.0
    }
    return (
      state.profile.account.tweetsSubmittedThisMonth /
      state.profile.account.allowedMonthlyTweets
    )
  },
  // Threads
  threadsAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_THREADS)
  },
  // Instagram
  instagramPostsRemainingThisPeriod(state) {
    if (!state.profile) {
      return 0
    }
    return (
      state.profile.account.allowedMonthlyNewPlatformPosts -
      state.profile.account.instagramPostsSubmittedThisMonth
    )
  },
  instagramAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_INSTAGRAM)
  },
  hasInstagramAccountsThatCanCreateStories(state, getters) {
    return getters.instagramAccounts.some(
      (account) =>
        account.platformFeatures && account.platformFeatures.create_stories
    )
  },
  isConnectedToInstagram(state, getters) {
    return getters.instagramAccounts.length > 0
  },
  // Facebook
  facebookAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_FACEBOOK)
  },
  // TikTok
  tiktokAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_TIKTOK)
  },
  isConnectedToTikTok(state, getters) {
    return getters.tiktokAccounts.length > 0
  },
  // LinkedIn
  linkedinAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_LINKEDIN)
  },
  // YouTube
  youtubeAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_YOUTUBE)
  },
  // Pinterest
  pinterestAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_PINTEREST)
  },
  // Bluesky
  blueskyAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_BLUESKY)
  },
  isConnectedToBluesky(state, getters) {
    return getters.blueskyAccounts.length > 0
  },
  // Tumblr
  tumblrAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_TUMBLR)
  },
  // Mastodon
  mastodonAccounts(state, getters) {
    return getters.platformAccounts(PLATFORM_CODE_MASTODON)
  },
  isConnectedToMastodon(state, getters) {
    return getters.isConnectedToPlatform(PLATFORM_CODE_MASTODON)
  },
  platformAccounts: (state, getters) => (platform) => {
    if (!state.profile) {
      return []
    }

    if (platform.toLowerCase() === 'reddit') {
      return getters.linkedRedditAccounts
    }

    return state.profile.socialAccounts.filter(
      (a) => a.platform.toLowerCase() === platform.toLowerCase()
    )
  },
  totalAccounts(state) {
    return state.profile.socialAccounts.length
  },
  canConnectAdditionalAccounts(state, getters) {
    return (
      state.profile &&
      state.profile.account &&
      (isUnlimited(state.profile.account.allowedSocialAccounts) ||
        state.profile.account.allowedSocialAccounts > getters.totalAccounts) &&
      getters.isAccountOwner
    )
  },
  enabledRedditAccounts(state, getters) {
    return getters.linkedRedditAccounts.filter((a) => a.enabled)
  },
  hasAccountScopeEnabled: (state, getters) => (username) => {
    if (typeof username !== 'string') {
      return
    }
    const account = getters.linkedRedditAccounts.find(
      (a) => a.username.toLowerCase() === username.toLowerCase()
    )
    if (!account) {
      return false
    }
    return account.hasAccountScopeEnabled
  },
  hasModconfigScopeEnabled: (state, getters) => (username) => {
    if (typeof username !== 'string') {
      return
    }
    const account = getters.linkedRedditAccounts.find(
      (a) => a.username.toLowerCase() === username.toLowerCase()
    )
    if (!account) {
      return false
    }
    return account.hasModconfigScopeEnabled
  },
  hasModpostsScopeEnabled: (state, getters) => (username) => {
    if (typeof username !== 'string') {
      return
    }
    const account = getters.linkedRedditAccounts.find(
      (a) => a.username.toLowerCase() === username.toLowerCase()
    )
    if (!account) {
      return false
    }
    return account.hasModpostsScopeEnabled
  },
  monthlyPostUsagePercentage(state) {
    if (!state.profile) {
      return 0.0
    }
    return (
      state.profile.account.postsSubmittedThisMonth /
      state.profile.account.allowedMonthlyPosts
    )
  },
  hasConfirmedEmail(state) {
    return !!(state.profile && state.profile.email)
  },
  hasPendingEmailConfirmation(state) {
    return !!(state.profile && state.profile.pendingEmail)
  },
  autoSpaceIntervals(state) {
    if (!state.profile || !state.profile.postSettings) {
      return []
    }

    let intervals = state.profile.postSettings.autoSpaceIntervals.map(
      (interval) => {
        return {
          amount: interval.amount,
          unit: interval.unit,
          label: `${interval.amount} ${pluralize(
            interval.amount,
            interval.unit
          )}`
        }
      }
    )

    return intervals
  },
  weekStart(state) {
    // Sunday = 0, Monday = 1.
    return state.profile?.weekStart || 0
  },
  totalUnreadCount(state) {
    return state.totalUnreadCount
  },
  canAccessInboxes(state) {
    return ['FULL', 'COMMENTS_ONLY', 'MESSAGES_ONLY'].includes(
      state.profile?.inboxPermission
    )
  },
  canAccessCommentsInbox(state) {
    return ['FULL', 'COMMENTS_ONLY'].includes(state.profile?.inboxPermission)
  },
  canAccessMessagesInbox(state) {
    return ['FULL', 'MESSAGES_ONLY'].includes(state.profile?.inboxPermission)
  },
  isAgencyOrHigher(state) {
    return state.profile.account.plan.monthlyPrice >= 99.0
  },
  canAccessSubredditManager(state) {
    return state.profile.subredditManagerPermission !== 'NONE'
  },
  canUpdateSubredditManager(state) {
    return state.profile.subredditManagerPermission === 'FULL'
  }
}
